import React, { FC, useState } from "react";
import { Box, Collapse, LinearProgress, Tooltip, Typography } from "@mui/material";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import moment from "moment";
import { formatCurrency } from "../../utils/formatCurrency";
import { useUser } from "../../hooks/useUser";
import { hidePricing } from "../../utils";

// Package engagement context attached by the backend when the order's SOW has
// a package. The package's flat sell price is the billed amount; the computed
// order total stays as an internal "value delivered" reference.
export interface OrderPackageContext {
    name: string;
    sellPrice: number;
    maxSkus: number | null;
    termMonths?: number | null;
    startDate?: string | null;
    endDate?: string | null;
    paymentSchedule?: string | null;
    skusUsed?: number;
    // Preview-only: slots the current draft would consume, and the projection
    // including them.
    draftSkus?: number;
    projectedSkusUsed?: number;
    overCapacity?: boolean;
    pastTerm?: boolean;
    coveredByPackage?: boolean;
    // Cost sheet frozen onto the SOW when the package was attached (absent on
    // engagements from before line items existed).
    lineItems?: {
        label: string;
        hours: number | null;
        rate: number | null;
        cost: number;
        notes?: string | null;
    }[];
}

export interface OrderPricingBreakdownData {
    projectCount: number;
    // Real bundling savings — present on snapshots from the updated recompute.
    // `savings`/`effectiveDiscountPct` capture the actual order-level discount
    // (project-count + matching-product tiers), unlike the legacy headline %.
    listTotal?: number;
    savings?: number;
    effectiveDiscountPct?: number;
    projectCountDiscountPct?: number;
    // Net manual production adjustments across the order (signed). Included in
    // the total, excluded from savings.
    adjustmentsTotal?: number;
    // Legacy field from older order snapshots: headline project-count tier %
    // only, ignores matching-product savings. Used as a display fallback.
    discountPct?: number;
    projectIds: number[];
    total: number;
    package?: OrderPackageContext;
}

const GOLD = "var(--accent)";

// Dense label/value row for the tooltip layout's full breakdown.
const TipRow: FC<{ label: string; value: string; muted?: boolean; bold?: boolean }> = ({
    label,
    value,
    muted,
    bold
}) => (
    <Box className="flex justify-between items-baseline gap-3">
        <Typography
            sx={{
                fontSize: "12px",
                color: muted ? "var(--muted)" : "var(--text-2)",
                fontWeight: bold ? 700 : 450,
                fontFamily: "CircularStd"
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: "12px",
                color: muted ? "var(--muted)" : "var(--text)",
                fontWeight: bold ? 700 : 500,
                fontFamily: "CircularStd"
            }}
        >
            {value}
        </Typography>
    </Box>
);

// Consumption bar + term line for a packaged order. `projectedSkusUsed` (from
// previews) wins over the saved `skusUsed`.
const PackageUsage: FC<{ pkg: OrderPackageContext }> = ({ pkg }) => {
    const used = pkg.projectedSkusUsed ?? pkg.skusUsed ?? 0;
    const max = pkg.maxSkus;
    const over = max !== null && max !== undefined && used > max;
    const pastTerm = !!pkg.endDate && new Date(`${pkg.endDate}T00:00:00`) < new Date();
    return (
        <Box className="flex flex-col gap-1">
            {max ? (
                <>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min((used / max) * 100, 100)}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "var(--track)",
                            "& .MuiLinearProgress-bar": {
                                bgcolor: over ? "var(--down)" : GOLD,
                                borderRadius: 3
                            }
                        }}
                    />
                    <Typography sx={{ fontSize: "12px", color: over ? "var(--down)" : "var(--accent-text)", fontFamily: "CircularStd" }}>
                        {used} of {max} SKUs {pkg.projectedSkusUsed !== undefined ? "after this order" : "used"}
                        {over ? " — over capacity" : ""}
                    </Typography>
                </>
            ) : null}
            {pkg.endDate && (
                <Typography sx={{ fontSize: "12px", color: pastTerm ? "var(--down)" : "var(--accent-text)", fontFamily: "CircularStd" }}>
                    {pastTerm ? "Term ended" : "Term ends"} {moment(pkg.endDate).format("MMM YYYY")}
                    {pastTerm ? " — past term" : ""}
                </Typography>
            )}
        </Box>
    );
};

// Compact expandable cost sheet for a packaged engagement — the line items
// frozen at attach time, matching the sell sheet the client was shown.
// Collapsed by default.
const PackageCostSheet: FC<{ pkg: OrderPackageContext }> = ({ pkg }) => {
    const [open, setOpen] = useState(false);
    const items = pkg.lineItems ?? [];
    if (items.length === 0) return null;
    const totalCost = items.reduce((s, r) => s + (Number(r.cost) || 0), 0);
    return (
        <Box>
            <Typography
                onClick={() => setOpen((v) => !v)}
                sx={{ fontSize: "12px", color: "var(--accent-text)", fontFamily: "CircularStd", cursor: "pointer" }}
            >
                {open ? "hide cost sheet ▴" : "what's included? ▾"}
            </Typography>
            <Collapse in={open}>
                <Box sx={{ pl: 1.5, pt: 0.5, borderLeft: "2px solid var(--border)", ml: 0.5 }}>
                    {items.map((r, i) => (
                        <Box key={`${r.label}-${i}`} className="flex justify-between items-baseline" sx={{ py: "1px" }}>
                            <Typography sx={{ fontSize: "12px", color: "var(--accent-text)", fontFamily: "CircularStd" }}>
                                {r.label}
                                {r.hours != null && r.rate != null ? ` · ${r.hours} hrs × $${r.rate}` : ""}
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "var(--accent-text)", fontFamily: "CircularStd" }}>
                                {formatCurrency(r.cost)}
                            </Typography>
                        </Box>
                    ))}
                    <Box className="flex justify-between items-baseline" sx={{ py: "1px" }}>
                        <Typography sx={{ fontSize: "12px", color: "var(--accent-text)", fontWeight: 700, fontFamily: "CircularStd" }}>
                            Total resource value
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "var(--accent-text)", fontWeight: 700, fontFamily: "CircularStd" }}>
                            {formatCurrency(totalCost)}
                        </Typography>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

interface Props {
    pricingTotal: string | null | undefined;
    pricingBreakdown: OrderPricingBreakdownData | null | undefined;
    pricingSnapshottedAt?: string | null;
    layout?: "summary" | "tooltip";
    /**
     * The priced order (or any entity carrying brand/brands with rmn flags).
     * TDC-1591: RMN-deal work shows no pricing at all.
     */
    order?: any;
}

export const OrderPricingBreakdown: FC<Props> = ({
    pricingTotal,
    pricingBreakdown,
    pricingSnapshottedAt,
    layout = "summary",
    order,
}) => {
    const { user, role } = useUser();

    // Nothing about an order's money reaches a brand-side viewer (TDC-1578)
    // or anyone at all on RMN-deal work (TDC-1591) — package terms, the
    // internal "value delivered" reference and the savings math alike.
    if (hidePricing(role, user, order)) {
        if (layout === "tooltip") return <span>—</span>;
        return null;
    }

    if (pricingTotal === null || pricingTotal === undefined) {
        if (layout === "tooltip") return <span>—</span>;
        return (
            <Typography sx={{ fontSize: "14px", color: "var(--muted)", fontFamily: "CircularStd" }}>
                Pricing not yet calculated.
            </Typography>
        );
    }

    const total = formatCurrency(pricingTotal);
    const b = pricingBreakdown;

    // Prefer the real bundling savings when the snapshot carries them; fall
    // back to the legacy headline tier % for older order snapshots.
    const savings = b?.savings;
    const effectivePct = b?.effectiveDiscountPct;
    const hasSavings = savings !== undefined && savings > 0;
    const legacyPct = b?.discountPct;
    const adjustments = b?.adjustmentsTotal ?? 0;
    const adjustmentsLabel =
        adjustments !== 0
            ? `Includes ${adjustments > 0 ? "+" : "−"}${formatCurrency(Math.abs(adjustments))} production adjustment${Math.abs(adjustments) === 1 ? "" : "s"}`
            : null;

    const pkg = b?.package;

    // Tooltip layout + packaged SOW: package icon whose hover shows the
    // engagement terms ONLY — the à-la-carte quote math is not shown to
    // anyone (internal included) for packaged work.
    if (layout === "tooltip" && pkg) {
        const used = pkg.skusUsed ?? 0;
        const pastTerm = !!pkg.endDate && new Date(`${pkg.endDate}T00:00:00`) < new Date();
        const tip = (
            <Box sx={{ py: 0.5 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>
                    {pkg.name} package
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                    {formatCurrency(pkg.sellPrice)} flat for the whole engagement
                    {pkg.paymentSchedule ? ` · ${pkg.paymentSchedule}` : ""}
                </Typography>
                {pkg.maxSkus ? (
                    <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                        {used} of {pkg.maxSkus} SKUs used
                    </Typography>
                ) : null}
                {pkg.endDate && (
                    <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
                        Term {pastTerm ? "ended" : "ends"} {moment(pkg.endDate).format("MMM YYYY")}
                    </Typography>
                )}
            </Box>
        );
        return (
            <Tooltip
                title={tip}
                arrow
                placement="left"
                enterDelay={200}
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: "var(--surface)",
                            color: "var(--text)",
                            border: "1px solid var(--border)",
                            borderRadius: "12px",
                            boxShadow: "var(--sh-2)",
                            p: "14px 16px",
                            maxWidth: 340
                        }
                    },
                    arrow: { sx: { color: "var(--surface)" } }
                }}
            >
                <Inventory2OutlinedIcon sx={{ fontSize: 20, color: "var(--accent-text)", cursor: "help" }} />
            </Tooltip>
        );
    }

    if (layout === "tooltip") {
        const tip = b ? (
            <Box sx={{ minWidth: 220, py: 0.5 }}>
                <TipRow label={`Projects (${b.projectCount})`} value="" />
                {b.listTotal !== undefined && (
                    <TipRow label="List total" value={formatCurrency(b.listTotal)} />
                )}
                {hasSavings ? (
                    <TipRow
                        label={`Bundling discount${effectivePct ? ` (${effectivePct.toFixed(0)}% off)` : ""}`}
                        value={`−${formatCurrency(savings)}`}
                        muted
                    />
                ) : legacyPct && legacyPct > 0 ? (
                    <TipRow label="Order discount" value={`${legacyPct}%`} muted />
                ) : null}
                {adjustments !== 0 && (
                    <TipRow
                        label="Production adjustments"
                        value={`${adjustments > 0 ? "+" : "−"}${formatCurrency(Math.abs(adjustments))}`}
                        muted
                    />
                )}
                <Box sx={{ borderTop: "1px solid var(--border)", mt: 0.5, pt: 0.5 }}>
                    <TipRow label="Order total" value={total} bold />
                </Box>
                {pricingSnapshottedAt && (
                    <Typography sx={{ fontSize: "11px", color: "var(--muted)", fontStyle: "italic", mt: 0.5 }}>
                        Quoted {moment(pricingSnapshottedAt).fromNow()}
                    </Typography>
                )}
            </Box>
        ) : (
            <Typography sx={{ fontSize: "12px" }}>Breakdown unavailable.</Typography>
        );
        return (
            <Tooltip
                title={tip}
                arrow
                placement="left"
                enterDelay={200}
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: "var(--surface)",
                            color: "var(--text)",
                            border: "1px solid var(--border)",
                            borderRadius: "12px",
                            boxShadow: "var(--sh-2)",
                            p: "14px 16px",
                            maxWidth: 400
                        }
                    },
                    arrow: { sx: { color: "var(--surface)" } }
                }}
            >
                <RequestQuoteOutlinedIcon sx={{ fontSize: 20, color: "var(--accent-text)", cursor: "help" }} />
            </Tooltip>
        );
    }

    return (
        <Box className="flex flex-col gap-2 p-4 border border-border-token rounded-2xl bg-surface">
            {pkg && (
                <Box
                    className="flex flex-col gap-1.5 p-3 rounded-xl"
                    sx={{ bgcolor: "var(--tint)", border: `1px solid ${GOLD}` }}
                >
                    <Box className="flex justify-between items-baseline">
                        <Typography sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: "14px", color: "var(--accent-text)" }}>
                            Covered by {pkg.name} package
                        </Typography>
                        <Typography sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: "18px", color: "var(--text)" }}>
                            {formatCurrency(pkg.sellPrice)}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "12px", color: "var(--accent-text)", fontFamily: "CircularStd" }}>
                        Billed flat for the whole engagement
                        {pkg.paymentSchedule ? ` · ${pkg.paymentSchedule}` : ""}
                    </Typography>
                    <PackageUsage pkg={pkg} />
                    <PackageCostSheet pkg={pkg} />
                </Box>
            )}
            <Box className="flex justify-between items-baseline">
                <Typography sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: "15px", color: "var(--text)" }}>
                    {pkg ? "À-la-carte value (internal reference)" : "Order Total"}
                </Typography>
                <Typography sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: "20px", color: "var(--text)" }}>
                    {total}
                </Typography>
            </Box>
            {b && (
                <Box className="flex flex-col gap-1">
                    <Typography sx={{ fontSize: "13px", color: "var(--text-2)", fontFamily: "CircularStd" }}>
                        {b.projectCount} project{b.projectCount === 1 ? "" : "s"}
                    </Typography>
                    {hasSavings ? (
                        <Typography sx={{ fontSize: "13px", color: "var(--up)", fontFamily: "CircularStd" }}>
                            You save {formatCurrency(savings)}
                            {effectivePct ? ` (${effectivePct.toFixed(0)}% off)` : ""}
                        </Typography>
                    ) : legacyPct && legacyPct > 0 ? (
                        <Typography sx={{ fontSize: "13px", color: "var(--text-2)", fontFamily: "CircularStd" }}>
                            {legacyPct}% order quantity discount applied
                        </Typography>
                    ) : null}
                    {adjustmentsLabel && (
                        <Typography sx={{ fontSize: "12px", color: "var(--muted)", fontFamily: "CircularStd" }}>
                            {adjustmentsLabel}
                        </Typography>
                    )}
                </Box>
            )}
            {pricingSnapshottedAt && (
                <Typography
                    sx={{ fontSize: "12px", color: "var(--muted)", fontFamily: "CircularStd", fontStyle: "italic", mt: 1 }}
                >
                    Quoted {moment(pricingSnapshottedAt).fromNow()}
                </Typography>
            )}
        </Box>
    );
};
